import {
  getSubRoofSlopes,
  getSubRoofRightSlopes,
  getLeftSpan,
  buildSlopes,
  getBaseToPeak,
  flattenSlopes,
} from "building-generation/elements/roofs/roofSlopes";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import { RoofTruncationType } from "building-types/settings/roofBaseTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { roofRails2DNew } from "doc/utils/roofTools";
import { polyLine2Morph, polyLine2Copy, polyLine2ByTwoPoints } from "geom/polyLine2";

/**
 * Generates the slopes of a multi-gable roof.
 */
export function generateRoofVolumeSlopes({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const {
    roofVersion,
    roofType,
    rails2D,
    heelHeightOffsets,
    span,
    baseHeight,
    startOverlap,
    endOverlap,
    rectangularOverhangs,
    perimeters,
    startTruncation,
    endTruncation,
  } = roofVolume;
  const { roofInputSettings, roofData, neighborRoofs, flipBaseSlopes, inputPerimeters, flipTruncationOrder } =
    roofVolumeOptions;
  const { flattenLeftSideSlopes, flattenRightSideSlopes, roofSideOverrides, roofEdgeOverrides } = roofData;
  const { roofSettings } = roofInputSettings;
  const { exactPerimeter } = inputPerimeters;
  const { baseLeftOverhang, baseRightOverhang } = rectangularOverhangs;
  const { boundingPerimeter, startEdge } = perimeters;

  const slopeOptions = {
    exactPerimeter,
    boundingPerimeter,
    startEdge,
    roofSettings,
    roofSideOverrides,
    roofEdgeOverrides,
  };

  let leftSideSlopeOptions = getSubRoofSlopes(slopeOptions);
  let rightSideSlopeOptions = getSubRoofRightSlopes(slopeOptions);

  if (roofType !== GableRoofType.DEFAULT) {
    const leftSpan = getLeftSpan({
      leftSideSlopeOptions,
      rightSideSlopeOptions,
      span,
      heelHeightOffsets,
      roofOverhang: baseLeftOverhang,
    });

    const rightSpan = span - leftSpan;
    const halfSpan = span / 2;

    if (roofVersion >= 4) {
      leftSideSlopeOptions = getSubRoofSlopes(slopeOptions);
      rightSideSlopeOptions = getSubRoofSlopes(slopeOptions);
    } else {
      if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
        if (leftSpan < halfSpan) {
          rightSideSlopeOptions = getSubRoofSlopes(slopeOptions);
        }
      } else {
        if (rightSpan < halfSpan) {
          leftSideSlopeOptions = getSubRoofRightSlopes(slopeOptions);
        }
      }
    }
  }

  const { startNeighbor, endNeighbor } = neighborRoofs;
  const { slopes: startNeighborSlopes, baseHeight: startRoofBaseHeight } = startNeighbor;
  const { slopes: endNeighborSlopes, baseHeight: endRoofBaseHeight } = endNeighbor;

  const startRelativeHeight = baseHeight - startRoofBaseHeight;
  const endRelativeHeight = baseHeight - endRoofBaseHeight;

  const leftSpan = getLeftSpan({
    leftSideSlopeOptions,
    rightSideSlopeOptions,
    span,
    heelHeightOffsets,
    roofOverhang: baseLeftOverhang,
  });

  const rightSpan = span - leftSpan;

  const leftSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: leftSideSlopeOptions,
    oppSideSlopes: rightSideSlopeOptions,
    startNeighborSlopes,
    endNeighborSlopes,
    startTruncation,
    endTruncation,
    flipTruncationOrder,
    startRelativeHeight,
    endRelativeHeight,
    halfSpan: leftSpan,
    oppHalfSpan: rightSpan,
    roofOverhang: baseLeftOverhang,
    oppRoofOverhang: baseRightOverhang,
    startOverlap,
    endOverlap,
    heightOffset: heelHeightOffsets.leftOffset,
    oppHeightOffset: heelHeightOffsets.rightOffset,
  });

  const rightSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: rightSideSlopeOptions,
    oppSideSlopes: leftSideSlopeOptions,
    startNeighborSlopes,
    endNeighborSlopes,
    startTruncation,
    endTruncation,
    flipTruncationOrder,
    startRelativeHeight,
    endRelativeHeight,
    halfSpan: rightSpan,
    oppHalfSpan: leftSpan,
    roofOverhang: baseRightOverhang,
    oppRoofOverhang: baseLeftOverhang,
    startOverlap,
    endOverlap,
    heightOffset: heelHeightOffsets.rightOffset,
    oppHeightOffset: heelHeightOffsets.leftOffset,
  });

  const baseSlopeOptions = flipBaseSlopes ? rightSideSlopeOptions : leftSideSlopeOptions;
  const baseSlopes = buildSlopes({
    heelHeightOffsets,
    slopes: baseSlopeOptions,
    startNeighborSlopes,
    endNeighborSlopes,
    startTruncation,
    endTruncation,
    flipTruncationOrder,
    startRelativeHeight,
    endRelativeHeight,
    halfSpan: span * 0.5,
    roofOverhang: baseLeftOverhang,
    startOverlap,
    endOverlap,
  });

  const baseToPeak = getBaseToPeak(leftSlopes);

  const leftSideSlopes =
    flattenLeftSideSlopes && startTruncation.type === RoofTruncationType.NONE ? flattenSlopes(leftSlopes) : leftSlopes;
  const rightSideSlopes =
    flattenRightSideSlopes && startTruncation.type === RoofTruncationType.NONE
      ? flattenSlopes(rightSlopes)
      : rightSlopes;

  const { leftRail, rightRail } = rails2D;
  const peakRail = polyLine2Morph(leftRail, rightRail, rightSpan / span);
  const peakRailNoExtension = polyLine2Copy(peakRail);
  const startRail = polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]);
  const endRail = polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]);

  const newRails2D = roofRails2DNew(leftRail, rightRail, startRail, endRail, peakRail, peakRailNoExtension);

  return {
    roofVolume: {
      baseToPeak,
      leftSpan,
      rightSpan,
      baseSlopes,
      leftSideSlopes,
      rightSideSlopes,
      rails2D: newRails2D,
      baseSlopeOptions,
    },
  };
}
